/**
 * Assessment Queue View
 *
 * Displays pending assessments for auditors to review.
 * Allows filtering by status and date, sorting, and starting reviews.
 *
 * Design Doc Reference: Lines 330-331 (Persona C: Compliance Auditor - Step 1: Queue)
 * - View pending submissions
 * - Sort/filter capabilities
 *
 * @version 1.0.0
 * @created 2025-12-11
 * @epic DC-523 Vendor Certification Program
 * @task DC-523 Gap Remediation Phase 1
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/vendor.css';

// =============================================================================
// TYPES
// =============================================================================

type AssessmentStatus = 'submitted' | 'under_review';

interface AssessmentWithProduct {
  assessment_id: string;
  product_id: string;
  product_name: string;
  product_version?: string;
  vendor_id: string;
  vendor_name: string;
  status: AssessmentStatus;
  requested_at: string;
  submitted_at?: string;
  deadline?: string;
  reviewed_by?: string;
  scores?: {
    ai_readiness?: number;
    stations?: {
      data_quality?: { score: number };
      ml_readiness?: { score: number };
      ontology?: { score: number };
      fair?: { score: number };
    };
  };
}

interface QueueStats {
  submitted: number;
  under_review: number;
  total: number;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(dateStr);
};

const getStatusBadgeClass = (status: AssessmentStatus): string => {
  switch (status) {
    case 'submitted':
      return 'status-badge status-submitted';
    case 'under_review':
      return 'status-badge status-review';
    default:
      return 'status-badge';
  }
};

const getStatusLabel = (status: AssessmentStatus): string => {
  switch (status) {
    case 'submitted':
      return 'Awaiting Review';
    case 'under_review':
      return 'Under Review';
    default:
      return status;
  }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AssessmentQueue: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [assessments, setAssessments] = useState<AssessmentWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<QueueStats>({ submitted: 0, under_review: 0, total: 0 });

  // Filters from URL
  const statusFilter = (searchParams.get('status') as AssessmentStatus | 'all') || 'all';
  const sortBy = searchParams.get('sort') || 'submitted_at';
  const sortOrder = searchParams.get('order') || 'desc';

  // Update URL params
  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all' || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  // Fetch assessments
  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build query for queue items (submitted and under_review only)
      const statuses = statusFilter === 'all'
        ? 'submitted,under_review'
        : statusFilter;

      const response = await fetch(`/api/assessments?status=${statuses}&limit=100`);
      if (!response.ok) {
        throw new Error('Failed to fetch assessments');
      }

      const data = await response.json();
      let items: AssessmentWithProduct[] = data.assessments || [];

      // Client-side sorting
      items.sort((a, b) => {
        let aVal: string | number = '';
        let bVal: string | number = '';

        switch (sortBy) {
          case 'submitted_at':
            aVal = a.submitted_at || a.requested_at;
            bVal = b.submitted_at || b.requested_at;
            break;
          case 'vendor_name':
            aVal = a.vendor_name.toLowerCase();
            bVal = b.vendor_name.toLowerCase();
            break;
          case 'product_name':
            aVal = a.product_name.toLowerCase();
            bVal = b.product_name.toLowerCase();
            break;
          case 'status':
            aVal = a.status;
            bVal = b.status;
            break;
          default:
            aVal = a.submitted_at || a.requested_at;
            bVal = b.submitted_at || b.requested_at;
        }

        if (sortOrder === 'asc') {
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        }
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      });

      setAssessments(items);

      // Calculate stats
      const submitted = items.filter(a => a.status === 'submitted').length;
      const underReview = items.filter(a => a.status === 'under_review').length;
      setStats({
        submitted,
        under_review: underReview,
        total: items.length,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  // Start review handler
  const handleStartReview = async (assessmentId: string) => {
    try {
      // Call start-review API to transition status
      const response = await fetch(`/api/assessments/${assessmentId}/start-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to start review');
      }

      // Navigate to review page
      navigate(`/assessments/${assessmentId}/review`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start review');
    }
  };

  // Continue review handler (for already under_review items)
  const handleContinueReview = (assessmentId: string) => {
    navigate(`/assessments/${assessmentId}/review`);
  };

  return (
    <div className="vendor-dashboard assessment-queue">
      {/* Breadcrumb Navigation */}
      <nav className="vendor-breadcrumb">
        <button className="breadcrumb-link" onClick={() => navigate('/home')}>
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <button className="breadcrumb-link" onClick={() => navigate('/vendors')}>
          Vendor Certification
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Assessment Queue</span>
      </nav>

      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Assessment Queue</h1>
          <p className="subtitle">
            Review pending vendor assessments for certification decisions
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="summary-cards">
        <div
          className={`summary-card ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => updateFilter('status', 'all')}
          style={{ cursor: 'pointer' }}
        >
          <div className="summary-value">{stats.total}</div>
          <div className="summary-label">Total in Queue</div>
        </div>
        <div
          className={`summary-card ${statusFilter === 'submitted' ? 'active' : ''}`}
          onClick={() => updateFilter('status', 'submitted')}
          style={{ cursor: 'pointer' }}
        >
          <div className="summary-value" style={{ color: 'var(--warning, #f59e0b)' }}>
            {stats.submitted}
          </div>
          <div className="summary-label">Awaiting Review</div>
        </div>
        <div
          className={`summary-card ${statusFilter === 'under_review' ? 'active' : ''}`}
          onClick={() => updateFilter('status', 'under_review')}
          style={{ cursor: 'pointer' }}
        >
          <div className="summary-value" style={{ color: 'var(--info, #3b82f6)' }}>
            {stats.under_review}
          </div>
          <div className="summary-label">Under Review</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label htmlFor="status-filter" className="filter-label">Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="all">All Pending</option>
            <option value="submitted">Awaiting Review</option>
            <option value="under_review">Under Review</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sort-filter" className="filter-label">Sort by:</label>
          <select
            id="sort-filter"
            value={sortBy}
            onChange={(e) => updateFilter('sort', e.target.value)}
          >
            <option value="submitted_at">Submitted Date</option>
            <option value="vendor_name">Vendor Name</option>
            <option value="product_name">Product Name</option>
            <option value="status">Status</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="order-filter" className="filter-label">Order:</label>
          <select
            id="order-filter"
            value={sortOrder}
            onChange={(e) => updateFilter('order', e.target.value)}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>

        <button
          className="btn btn-secondary btn-sm"
          onClick={fetchAssessments}
          title="Refresh list"
        >
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="error-state" style={{ marginBottom: '1rem' }}>
          <p style={{ color: 'var(--danger, #ef4444)' }}>{error}</p>
          <button className="btn btn-secondary" onClick={fetchAssessments}>
            Try Again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading assessments...</p>
        </div>
      ) : assessments.length === 0 ? (
        /* Empty State */
        <div className="empty-state">
          <div className="empty-state-icon">&#128203;</div>
          <h2>No Pending Assessments</h2>
          <p>
            There are no assessments waiting for review.
            {statusFilter !== 'all' && ' Try changing your filter settings.'}
          </p>
          {statusFilter !== 'all' && (
            <button
              className="btn btn-secondary"
              onClick={() => updateFilter('status', 'all')}
            >
              Show All
            </button>
          )}
        </div>
      ) : (
        /* Assessment Table */
        <div className="queue-table-container">
          <table className="queue-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Product</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>AI Readiness</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((assessment) => (
                <tr key={assessment.assessment_id}>
                  <td>
                    <button
                      className="link-button"
                      onClick={() => navigate(`/vendors/${assessment.vendor_id}`)}
                    >
                      {assessment.vendor_name}
                    </button>
                  </td>
                  <td>
                    <span className="product-name">{assessment.product_name}</span>
                    {assessment.product_version && (
                      <span className="product-version">v{assessment.product_version}</span>
                    )}
                  </td>
                  <td>
                    <span title={assessment.submitted_at ? formatDate(assessment.submitted_at) : ''}>
                      {formatRelativeTime(assessment.submitted_at || assessment.requested_at)}
                    </span>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(assessment.status)}>
                      {getStatusLabel(assessment.status)}
                    </span>
                  </td>
                  <td>
                    {assessment.scores?.ai_readiness != null ? (
                      <span className="score-value">
                        {Math.round(assessment.scores.ai_readiness)}%
                      </span>
                    ) : (
                      <span className="score-pending">Pending</span>
                    )}
                  </td>
                  <td>
                    {assessment.status === 'submitted' ? (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleStartReview(assessment.assessment_id)}
                      >
                        Start Review
                      </button>
                    ) : (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleContinueReview(assessment.assessment_id)}
                      >
                        Continue Review
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssessmentQueue;
